import React, { useState } from "react";
import { Input } from "../shadcn/ui/Input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../shadcn/ui/Select";
import { Combobox } from "../shadcn/ui/Combobox";
import { brand } from "../config/brand.config";
import { Textarea } from "../shadcn/ui/Textarea";
import { Checkbox } from "../shadcn/ui/Checkbox";
import { Switch } from "../shadcn/ui/Switch";
import { MainCategory, categoriesData } from "../config/productcat.config";
import { ReusableUploader } from "../components/ReusableUploader";

export interface DyanamicField {
  label: string;
  key: string;
  type:
    | "input"
    | "dropdownsearch"
    | "dropdown"
    | "file"
    | "categories"
    | "subcategories"
    | "number"
    | "checkbox"
    | "numberrange"
    | "switch"
    | "textarea";
  placeholder?: string;
  required?: boolean;
  options?: string[];
  data?: MainCategory[];
  multiple?: boolean;
  accept?: Record<string, string[]>;
}

interface FieldRendererProps {
  fields: DyanamicField[];
}

export const DynamicFieldRenderer: React.FC<FieldRendererProps> = ({
  fields,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>("");
  const [filesMap, setFilesMap] = useState<Record<string, File[]>>({});

  if (!fields.length) return null;

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {fields.map((field, index) => {
          const { key, label, type, placeholder, required, options } = field;

          const wrapperClass =
            type === "textarea" || type === "numberrange"
              ? "md:col-span-2"
              : "";

          switch (type) {
            case "input":
              return (
                <div key={`${key}-${index}`}>
                  <label className="block font-medium mb-1">
                    {label} {required && "*"}
                  </label>
                  <Input
                    placeholder={placeholder}
                    name={key}
                    required={required}
                    className="w-full"
                  />
                </div>
              );

            case "file":
              return (
                <ReusableUploader
                  key={`${key}-${index}`}
                  fieldKey={key}
                  label={label}
                  required={required}
                  multiple={!!field.multiple}
                  accept={field.accept}
                  filesMap={filesMap}
                  setFilesMap={setFilesMap}
                />
              );

            case "dropdownsearch":
              return (
                <div key={`${key}-${index}`} className={wrapperClass}>
                  <label className="block font-medium mb-1">
                    {label} {required && "*"}
                  </label>
                  <Combobox
                    options={(brand || []).map((b) => ({
                      value: b.name.toLowerCase(),
                      label: b.name,
                    }))}
                    placeholder={placeholder}
                    className="w-full h-auto bg-white"
                  />
                </div>
              );

            case "textarea":
              return (
                <div key={`${key}-${index}`} className="md:col-span-2">
                  <label className="block font-medium mb-1">
                    {label} {required && "*"}
                  </label>
                  <Textarea
                    placeholder={placeholder}
                    name={key}
                    required={required}
                    className="w-full min-h-[100px]"
                  />
                </div>
              );

            case "switch":
              return (
                <div
                  key={`${key}-${index}`}
                  className={`flex items-center justify-between py-2 ${wrapperClass}`}
                >
                  <label
                    htmlFor={key}
                    className="block font-medium text-sm cursor-pointer"
                  >
                    {label} {required && "*"}
                  </label>
                  <Switch id={key} name={key} />
                </div>
              );

            case "number":
              return (
                <div key={`${key}-${index}`} className={wrapperClass}>
                  <label className="block font-medium mb-1">
                    {label} {required && "*"}
                  </label>
                  <Input
                    type="number"
                    placeholder={placeholder}
                    name={key}
                    required={required}
                    className="w-full"
                    onKeyDown={(e) => {
                      if (
                        ![
                          "Backspace",
                          "Tab",
                          "ArrowLeft",
                          "ArrowRight",
                          "Delete",
                        ].includes(e.key) &&
                        isNaN(Number(e.key))
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
              );

            case "numberrange":
              return (
                <div key={`${key}-${index}`} className="md:col-span-2">
                  <label className="block font-medium mb-1">
                    {label} {required && "*"}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      type="number"
                      placeholder="Min"
                      name={`${key}_min`}
                      required={required}
                    />
                    <Input
                      type="number"
                      placeholder="Max"
                      name={`${key}_max`}
                      required={required}
                    />
                  </div>
                </div>
              );

            case "checkbox":
              return (
                <div
                  key={`${key}-${index}`}
                  className={`space-y-2 ${wrapperClass}`}
                >
                  <label className="block font-semibold text-base">
                    {label} {required && "*"}
                  </label>
                  <div className="flex flex-col gap-2">
                    {(options || []).map((opt, i) => {
                      const optId = `${key}_${i}`;
                      return (
                        <div
                          key={optId}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox id={optId} name={`${key}[]`} value={opt} />
                          <label
                            htmlFor={optId}
                            className="text-sm cursor-pointer"
                          >
                            {opt}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );

            case "dropdown":
              return (
                <div key={`${key}-${index}`} className={wrapperClass}>
                  <label className="block font-medium mb-1">
                    {label} {required && "*"}
                  </label>
                  <Select name={key}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {(options || []).map((opt, i) => (
                        <SelectItem
                          key={`${opt}-${i}`}
                          value={opt}
                          className="hover:bg-gray-100 outline-none"
                        >
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );

            case "categories":
              return (
                <div key={`${key}-${index}`} className={wrapperClass}>
                  <label className="block font-medium mb-1">
                    {label} {required && "*"}
                  </label>
                  <Select
                    name={key}
                    value={selectedCategory}
                    onValueChange={(val) => {
                      setSelectedCategory(val);
                      setSelectedSubCategory("");
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {categoriesData.map((cat) => (
                        <SelectItem
                          key={cat.id}
                          value={cat.id}
                          className=" hover:bg-gray-100 outline-none"
                        >
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );

            case "subcategories": {
              const selectedCatData = categoriesData.find(
                (cat) => cat.id === selectedCategory
              );
              const availableSubCategories =
                selectedCatData?.subCategories || [];

              return (
                <div key={`${key}-${index}`} className={wrapperClass}>
                  <label className="block font-medium mb-1">
                    {label} {required && "*"}
                  </label>
                  <Select
                    name={key}
                    value={selectedSubCategory}
                    onValueChange={(val) => setSelectedSubCategory(val)}
                    disabled={
                      !selectedCategory || availableSubCategories.length === 0
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={
                          !selectedCategory
                            ? "Select category first"
                            : availableSubCategories.length > 0
                              ? "Select sub category"
                              : "No available subcategories"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {availableSubCategories.map((sub) => (
                        <SelectItem
                          key={sub.id}
                          value={sub.id}
                          className=" hover:bg-gray-100 outline-none"
                        >
                          {sub.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            }
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
};

export default DynamicFieldRenderer;
